/**
 * Os limites do quadro branco quando o aparelho é iOS/iPadOS — e o bundle é o WEB.
 *
 * ## O defeito que este arquivo existe para fechar
 *
 * O BlockSuite tem um **modo de sobrevivência de zoom baixo** para iOS: abaixo de 50% de
 * zoom, durante um gesto, ele desliga as *stacking canvases* e desenha placeholders, para
 * não estourar o orçamento de composição do WKWebView.
 *
 * Esse modo é ligado por `IS_IOS`, que é detecção de **runtime** por user-agent
 * (`blocksuite/framework/global/src/env/index.ts`) — num iPad Safari ele é **verdadeiro**.
 *
 * A **configuração** que o torna seguro e, sobretudo, REVERSÍVEL é outra coisa: ela é
 * armada pelo **entrypoint**, e o próprio upstream escreve isso no `viewport.ts`:
 *
 * > *"Mobile entry points (…) override these once at startup, before any editor mounts.
 * > Desktop leaves these untouched."*
 *
 * Só dois arquivos armam: `apps/ios/src/setup.ts` e `apps/android/src/setup.ts`. E o iPad
 * **não recebe nenhum dos dois** — ele recebe o bundle `web`, porque
 * `core/selfhost/static.ts` chama `isMobile({ ua })` sem `tablet: true` (o patch `0002` já
 * tinha catalogado essa metade).
 *
 * O resultado medido no `dev` em 2026-09-16, com iPad emulado, no documento do operador:
 *
 * ```
 * repouso 100%     zooming=false  placeholder=false  canvases=7
 * DURANTE o gesto  zooming=true   placeholder=TRUE   canvases=1   ← 6 canvases desligados
 * 300ms depois     zooming=false  placeholder=false  canvases=7   ← recuperou (no Chromium)
 * ```
 *
 * O quadro do "durante" é exatamente o print do operador: molduras, rótulos e bolinhas de
 * apresentação presentes (são DOM), e todo o conteúdo do canvas virando retângulo vazio.
 *
 * > **O iPad rodava MEIO-ARMADO: os ramos do iOS disparavam, e a configuração deles não
 * > existia.** É a família das regras 8.1/8.4/8.52/8.60 do `CLAUDE.md` — código presente,
 * > correto e inerte — agora pelo lado do *entrypoint*.
 *
 * E a metade que faltava é justamente a que **traz de volta**: a recuperação robusta do
 * upstream vive dentro de `if (SKIP_REFRESH_DURING_GESTURE)`, que é `false` no bundle web.
 * O comentário dela nomeia o sintoma do operador com todas as letras:
 *
 * > *"If a gesture is still in-flight when the timer fires, reschedule instead of dropping.
 * > **Dropping here left connectors blank until a tap forced a synchronous refresh.**"*
 *
 * ## O que este arquivo NÃO copia do iOS, e por quê
 *
 * ⚠️ **`ZOOM_MIN` continua em 0.1.** O `ios/setup.ts` o sobe para `0.4`, e copiá-lo aqui
 * tiraria do operador exatamente o gesto que ele estava usando — o print dele é a **22%**.
 * O upstream aceita esse preço porque lá a exigência dura é não matar o processo; aqui a
 * queixa é o conteúdo sumir, e subir o piso resolveria o sintoma tirando a capacidade.
 *
 * Isso é seguro porque o teto de dpr **já cobre a faixa estendida**: os baldes são lidos de
 * baixo para cima com `zoom < limiar`, então `0.1` cai no mesmo `[0.5, 1]` que `0.45`. A
 * faixa 0.1–0.5 nasce com dpr 1, que é o lever de memória que o upstream mediu em aparelho
 * (~7.2 mp com dpr 2 contra ~1.8 mp com dpr 1).
 */

/** O que `viewport.ts` chama de `viewportRuntimeConfig` — só as chaves que tocamos. */
export type LimitesDoQuadro = {
  VIEWPORT_REFRESH_PIXEL_THRESHOLD: number;
  VIEWPORT_REFRESH_MAX_INTERVAL: number;
  SKIP_REFRESH_DURING_GESTURE: boolean;
  LOW_ZOOM_GESTURE_ACTIVE_BLOCK_LIMIT: number;
  OVERSCAN_RATIO: number;
  OVERSCAN_RATIO_BLOCK: number;
  POST_GESTURE_REFRESH_DELAY: number;
  CANVAS_DPR_CAP_BY_ZOOM: Array<[number, number]>;
  ZOOM_MIN?: number;
};

/**
 * Os valores do `ios/setup.ts` — **menos o `ZOOM_MIN`**, pela razão escrita acima.
 *
 * Congelado de propósito: são a referência de um arquivo do upstream, e alguém que os
 * alterasse por engano em runtime moveria o comportamento de todo iPad sem deixar diff.
 */
export const LIMITES_DE_IOS_NO_NAVEGADOR = Object.freeze({
  // Menos emissões durante o gesto: o viewport-element mantém o visual por um transform
  // de container, sem custo por bloco.
  VIEWPORT_REFRESH_PIXEL_THRESHOLD: 60,
  VIEWPORT_REFRESH_MAX_INTERVAL: 300,

  // A chave do card: é ela que INSTALA a recuperação pós-gesto que reagenda em vez de
  // desistir. Sem ela, a única recuperação é o tiro único do `zooming$`.
  SKIP_REFRESH_DURING_GESTURE: true,

  LOW_ZOOM_GESTURE_ACTIVE_BLOCK_LIMIT: 1,

  // Pré-pinta uma margem de 20% no caminho de CANVAS, para o gesto entrar em conteúdo já
  // rasterizado em vez de branco.
  OVERSCAN_RATIO: 0.2,
  // O de BLOCO fica em 0: cada bloco montado é uma subárvore composta própria, e alargar
  // isto foi o que dobrou a contagem de blocos ativos antes do kill de memória no iOS.
  OVERSCAN_RATIO_BLOCK: 0,

  // 800ms (o default de desktop) é a janela em que as stacking canvases seguem desligadas
  // depois do gesto. 220 é o valor que o upstream mediu em aparelho.
  POST_GESTURE_REFRESH_DELAY: 220,

  // Memória de canvas escala com dpr². Baldes lidos de baixo para cima; o primeiro
  // `zoom < limiar` vence.
  CANVAS_DPR_CAP_BY_ZOOM: [
    [0.5, 1],
    [0.8, 2],
  ] as Array<[number, number]>,
});

export type AmbienteDoQuadro = {
  /** `IS_IOS` do `@blocksuite/global/env` — runtime, por user-agent. */
  ehIOS: boolean;
  /** `IS_IPAD` do mesmo lugar: pega o iPad em "Request Desktop Website". */
  ehIPad: boolean;
};

export const MOTIVO = {
  ARMADO: 'armado',
  NAO_E_IOS: 'nao-e-ios',
  JA_ARMADO: 'ja-armado-pelo-shell-nativo',
} as const;

export type Motivo = (typeof MOTIVO)[keyof typeof MOTIVO];

/**
 * Já existe alguém armando isto?
 *
 * O `ios/setup.ts` e o `android/setup.ts` rodam no shell nativo e preenchem
 * `CANVAS_DPR_CAP_BY_ZOOM`. O default de desktop é a lista **vazia**, então "não-vazia"
 * é a assinatura de que um entrypoint nativo já passou por aqui.
 *
 * ⚠️ A pergunta é feita sobre o ESTADO, não sobre qual bundle é. Um teste de bundle seria
 * a segunda verdade da Regra #9 — e erraria no dia em que o upstream acrescentasse um
 * terceiro shell.
 */
export function jaArmado(alvo: Pick<LimitesDoQuadro, 'CANVAS_DPR_CAP_BY_ZOOM'>): boolean {
  return Array.isArray(alvo.CANVAS_DPR_CAP_BY_ZOOM) && alvo.CANVAS_DPR_CAP_BY_ZOOM.length > 0;
}

/**
 * Decide, sem efeito nenhum. Separado do `aplicar` para que o teste possa afirmar sobre a
 * decisão sem precisar de um objeto de configuração de verdade.
 */
export function decidir(
  ambiente: AmbienteDoQuadro,
  alvo: Pick<LimitesDoQuadro, 'CANVAS_DPR_CAP_BY_ZOOM'>
): Motivo {
  if (!ambiente.ehIOS && !ambiente.ehIPad) return MOTIVO.NAO_E_IOS;
  if (jaArmado(alvo)) return MOTIVO.JA_ARMADO;
  return MOTIVO.ARMADO;
}

/**
 * Aplica os limites NO OBJETO RECEBIDO — o `alvo` entra por parâmetro justamente para que
 * este módulo se teste sem importar o blocksuite e sem navegador.
 *
 * Devolve o motivo, sempre. Um `void` faria "não era iOS" e "apliquei" ficarem
 * indistinguíveis, que é o defeito silencioso que este arquivo existe para não repetir.
 */
export function aplicarLimitesDoQuadro(
  alvo: LimitesDoQuadro,
  ambiente: AmbienteDoQuadro
): Motivo {
  const motivo = decidir(ambiente, alvo);
  if (motivo !== MOTIVO.ARMADO) return motivo;

  const v = LIMITES_DE_IOS_NO_NAVEGADOR;
  alvo.VIEWPORT_REFRESH_PIXEL_THRESHOLD = v.VIEWPORT_REFRESH_PIXEL_THRESHOLD;
  alvo.VIEWPORT_REFRESH_MAX_INTERVAL = v.VIEWPORT_REFRESH_MAX_INTERVAL;
  alvo.SKIP_REFRESH_DURING_GESTURE = v.SKIP_REFRESH_DURING_GESTURE;
  alvo.LOW_ZOOM_GESTURE_ACTIVE_BLOCK_LIMIT = v.LOW_ZOOM_GESTURE_ACTIVE_BLOCK_LIMIT;
  alvo.OVERSCAN_RATIO = v.OVERSCAN_RATIO;
  alvo.OVERSCAN_RATIO_BLOCK = v.OVERSCAN_RATIO_BLOCK;
  alvo.POST_GESTURE_REFRESH_DELAY = v.POST_GESTURE_REFRESH_DELAY;
  // Cópia: a lista congelada não pode virar a instância viva que outro código leria.
  alvo.CANVAS_DPR_CAP_BY_ZOOM = v.CANVAS_DPR_CAP_BY_ZOOM.map(
    ([limiar, teto]) => [limiar, teto] as [number, number]
  );

  return MOTIVO.ARMADO;
}
